import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * App-shell footer: "powered by <supplier>" (#21262, US 21239 phase 3).
 *
 * The supplier is US, the platform vendor — NOT the tenant. The tenant's own logo sits top-left
 * (`app-logo`); this footer names whoever supplies the platform for that environment, so every
 * field is a per-environment branding setting (`BrandingSettings.poweredByName` / `.poweredByUrl`
 * and the `light`/`dark` `poweredByLogo`). All are optional: with nothing configured the footer
 * renders nothing at all.
 *
 * Presentational — the layouts resolve the branding and pass it in. A configured logo shows as a
 * wordmark (light/dark variants, CSS-switched by the `.dark` class LayoutComponent stamps on
 * `<html>`/`<body>`, same idiom as `app-logo`); without a logo the name shows as text. Either way
 * the supplier NAME is the accessible label, so a screen reader never announces a wrong brand.
 *
 * One <a> whose href/target/rel are attribute bindings rather than an @if with two branches: the
 * URL only arrives after branding loads, and an <a> without href is simply not a link for the
 * browser or assistive tech.
 *
 * The caption color is a component token with a `.dark` override. The prototype has no dark scheme;
 * the dark value is ours and is LIGHTER than the light one. The light value is #6c6c78, not the
 * prototype's #a2a2ad: that one measured 2.35:1 on the #f7f7f7 page background and 2.52:1 on white
 * (axe color-contrast, "Componenten/Powered by"), and 12.5px text needs 4.5:1. #6c6c78 keeps the
 * muted purple-grey hue and reaches 4.84:1 / 5.18:1.
 */
@Component({
  selector: 'dd-powered-by',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      --dd-powered-by-caption: #6c6c78;
      display: block;
    }
    :host-context(.dark) {
      --dd-powered-by-caption: #c2c2cb;
    }
  `,
  template: `
    @if (supplierName() || logoLightSrc() || logoDarkSrc()) {
      <div data-cy="powered-by" class="flex flex-col items-center gap-2 pt-10 pb-[46px] print:hidden">
        <span
          data-cy="powered-by-caption"
          class="text-[0.78125rem] font-medium leading-none text-[color:var(--dd-powered-by-caption)]">
          powered by
        </span>
        <!-- The supplier mark is the link's only content, so the link carries its own name. -->
        <a
          data-cy="powered-by-logo"
          class="block rounded"
          [attr.href]="url() || null"
          [attr.target]="url() ? '_blank' : null"
          [attr.rel]="url() ? 'noopener noreferrer' : null"
          [attr.aria-label]="linkLabel()">
          @if (logoLightSrc() || logoDarkSrc()) {
            @if (singleLogoFallback(); as src) {
              <!-- Only ONE scheme has a logo and there is no name to fall back to for the other
                   scheme (review round 2, #21239 follow-up) — there is nothing else to show
                   there, so this logo renders under BOTH schemes instead of leaving one empty. -->
              <img
                class="w-auto max-w-full shrink-0 object-contain"
                [style.height.px]="logoHeight()"
                [src]="src"
                [alt]="supplierName() || 'leverancier'" />
            } @else {
              <!-- Each scheme has its own slot: a logo when configured, otherwise the name falls
                   back so that scheme never renders an empty, clickable anchor (#21239 follow-up). -->
              @if (logoLightSrc()) {
                <img
                  class="w-auto max-w-full shrink-0 object-contain only-light-theme"
                  [style.height.px]="logoHeight()"
                  [src]="logoLightSrc()"
                  [alt]="supplierName() || 'leverancier'" />
              } @else if (supplierName()) {
                <span data-cy="powered-by-name" class="text-sm font-semibold leading-none only-light-theme">{{
                  supplierName()
                }}</span>
              }
              @if (logoDarkSrc()) {
                <img
                  class="w-auto max-w-full shrink-0 object-contain only-dark-theme"
                  [style.height.px]="logoHeight()"
                  [src]="logoDarkSrc()"
                  [alt]="supplierName() || 'leverancier'" />
              } @else if (supplierName()) {
                <span data-cy="powered-by-name" class="text-sm font-semibold leading-none only-dark-theme">{{
                  supplierName()
                }}</span>
              }
            }
          } @else {
            <span data-cy="powered-by-name" class="text-sm font-semibold leading-none">{{ supplierName() }}</span>
          }
        </a>
      </div>
    }
  `,
})
export class PoweredByComponent {
  /** Supplier name (`BrandingSettings.poweredByName`). Text fallback and the link's accessible name. */
  readonly name = input<string | null>(null);
  /** Light-scheme supplier logo (`branding.light.poweredByLogo.blobUrl`). Null = fall back to the name. */
  readonly logoLightSrc = input<string | null>(null);
  /** Dark-scheme supplier logo (`branding.dark.poweredByLogo.blobUrl`). */
  readonly logoDarkSrc = input<string | null>(null);
  /** Configured link target (`branding.poweredByUrl`). Empty/null = no link. */
  readonly url = input<string | null>(null);
  /** Wordmark height in px. Default matches the prototype's ~20px footer mark. */
  readonly logoHeight = input(20);

  /**
   * The name as the template and the fallbacks agree on it: trimmed, and null when blank. One
   * definition of "no name", so a whitespace-only `poweredByName` cannot take the per-scheme
   * name-fallback path and render an invisible span (or a blank anchor).
   */
  readonly supplierName = computed<string | null>(() => this.name()?.trim() || null);

  /** The link's accessible name, or null when it is not a link. */
  readonly linkLabel = computed<string | null>(() => {
    if (!this.url()) {
      return null;
    }
    const supplier = this.supplierName() || 'Leverancier';
    return `${supplier} (opent in nieuw tabblad)`;
  });

  /**
   * The one configured logo, when the OTHER scheme would otherwise render nothing at all: only
   * one of `logoLightSrc`/`logoDarkSrc` is set, and `name` — the per-scheme fallback everywhere
   * else — is also empty. `null` whenever either scheme has something of its own to show (its
   * own logo, or the name fallback), so the two-slot template below stays in charge.
   */
  readonly singleLogoFallback = computed<string | null>(() => {
    if (this.supplierName()) {
      return null;
    }
    // Not named `light`/`dark`: Tailwind scans .ts files, reads a negated `dark`/`light` variable
    // as an important-modifier class and emits empty-selector copies of Fuse's print styles.
    const lightSrc = this.logoLightSrc();
    const darkSrc = this.logoDarkSrc();
    if (lightSrc && !darkSrc) {
      return lightSrc;
    }
    if (darkSrc && !lightSrc) {
      return darkSrc;
    }
    return null;
  });
}
